import {
    type FC,
    createContext,
    useContext,
    useEffect,
    useRef,
    useState
} from 'react'

import './_modal.scss'
import { useTranslation } from 'react-i18next'

import { Icons } from '../helpers/icons'

interface ModalState {
    title: string
    withoutTitle: boolean
    withoutCloseIcon: boolean
    window: any
    customClass: string
    withScroll: boolean
    key: string
    keyboardAdjust: boolean
}

const emptyModalState: ModalState = {
    title: '',
    withoutTitle: false,
    withoutCloseIcon: false,
    window: null,
    customClass: '',
    withScroll: false,
    key: '',
    keyboardAdjust: false
}

type AddModal = (
    title: string,
    window: any,
    withoutTitle?: boolean,
    withoutCloseIcon?: boolean,
    customClass?: string,
    key?: string,
    keyboardAdjust?: boolean
) => void

interface IModalContextValues {
    addModal: AddModal
    clearModal: () => void

    addSecondaryModal: AddModal
    clearSecondaryModal: () => void

    addScroll: () => void
    removeScroll: () => void

    isOpen: boolean
    isSecondaryOpen: boolean

    key: string
    secondaryKey: string
}

const defaultValues: IModalContextValues = {
    addModal: () => {},
    clearModal: () => {},

    addSecondaryModal: () => {},
    clearSecondaryModal: () => {},

    addScroll: () => {},
    removeScroll: () => {},

    isOpen: false,
    isSecondaryOpen: false,

    key: '',
    secondaryKey: ''
}

export const ModalContext =
    createContext<IModalContextValues>(defaultValues)

export const ModalProvider: FC<{
    children?: any
}> = ({ children }) => {
    const [state, setState] =
        useState<ModalState>(emptyModalState)

    const [secondaryState, setSecondaryState] =
        useState<ModalState>(emptyModalState)

    const { t } = useTranslation()

    const addModal: AddModal = (
        title,
        window,
        withoutTitle = false,
        withoutCloseIcon = false,
        customClass = '',
        key = '',
        keyboardAdjust = false
    ) => {
        setState({
            title,
            window,
            withoutTitle,
            withoutCloseIcon,
            customClass,
            key,
            withScroll: false,
            keyboardAdjust
        })
    }

    const clearModal = () => {
        /*
         * If the main modal closes, any child modal should
         * also disappear.
         */
        setSecondaryState(emptyModalState)
        setState(emptyModalState)
    }

    const addSecondaryModal: AddModal = (
        title,
        window,
        withoutTitle = false,
        withoutCloseIcon = false,
        customClass = '',
        key = '',
        keyboardAdjust = false
    ) => {
        setSecondaryState({
            title,
            window,
            withoutTitle,
            withoutCloseIcon,
            customClass,
            key,
            withScroll: false,
            keyboardAdjust
        })
    }

    const clearSecondaryModal = () => {
        setSecondaryState(emptyModalState)
    }

    const addScroll = () => {
        setState(previous => ({
            ...previous,
            withScroll: true
        }))
    }

    const removeScroll = () => {
        setState(previous => ({
            ...previous,
            withScroll: false
        }))
    }

    const keyboardAdjustOpen =
        (Boolean(state.window) && state.keyboardAdjust) ||
        (Boolean(secondaryState.window) && secondaryState.keyboardAdjust)

    const anyModalOpen =
        Boolean(state.window) ||
        Boolean(secondaryState.window)

useEffect(() => {
    if (!keyboardAdjustOpen) return

    type VirtualKeyboard = {
        overlaysContent: boolean
        boundingRect: { height: number }
        addEventListener: (type: string, listener: () => void) => void
        removeEventListener: (type: string, listener: () => void) => void
    }

    const keyboard = (
        navigator as Navigator & {
            virtualKeyboard?: VirtualKeyboard
        }
    ).virtualKeyboard

    const update = () => {
        const vv = window.visualViewport

        const viewportHeight = vv?.height ?? window.innerHeight

        // Keyboard space already excluded by visualViewport
        const viewportReduction = Math.max(
            0,
            window.innerHeight - viewportHeight - (vv?.offsetTop ?? 0)
        )

        const keyboardHeight = keyboard?.boundingRect.height ?? 0

        // Don't subtract the same keyboard space twice.
        const shift = Math.max(
            keyboardHeight - viewportReduction,
            0
        )

        document.documentElement.style.setProperty(
            '--modal-keyboard-shift',
            `${shift}px`
        )
    }

    if (keyboard) {
        keyboard.overlaysContent = true
        keyboard.addEventListener('geometrychange', update)
    }

    window.visualViewport?.addEventListener('resize', update)
    window.addEventListener('resize', update)

    update()

    return () => {
        keyboard?.removeEventListener('geometrychange', update)
        window.visualViewport?.removeEventListener('resize', update)
        window.removeEventListener('resize', update)

        document.documentElement.style.removeProperty(
            '--modal-keyboard-shift'
        )
    }
}, [keyboardAdjustOpen])



    useEffect(() => {
        if (anyModalOpen) {
            document.body.classList.add(
                'modal__scrollblock'
            )
        } else {
            document.body.classList.remove(
                'modal__scrollblock'
            )
        }

        return () => {
            document.body.classList.remove(
                'modal__scrollblock'
            )
        }
    }, [anyModalOpen])

    useEffect(() => {
        if (!keyboardAdjustOpen) return
        const updateViewport = () => {
            const vv = window.visualViewport

            const rawHeight =
                vv?.height ??
                window.innerHeight

            const offsetTop =
                vv?.offsetTop ??
                0

            document.documentElement.style.setProperty(
                '--modal-vh',
                `${rawHeight}px`
            )

            document.documentElement.style.setProperty(
                '--modal-offset-top',
                `${offsetTop}px`
            )
        }

        updateViewport()

        window.visualViewport?.addEventListener(
            'resize',
            updateViewport
        )

        window.visualViewport?.addEventListener(
            'scroll',
            updateViewport
        )

        window.addEventListener(
            'resize',
            updateViewport
        )

        return () => {
            window.visualViewport?.removeEventListener(
                'resize',
                updateViewport
            )

            window.visualViewport?.removeEventListener(
                'scroll',
                updateViewport
            )

            window.removeEventListener(
                'resize',
                updateViewport
            )

            document.documentElement.style.removeProperty(
                '--modal-vh'
            )

            document.documentElement.style.removeProperty(
                '--modal-offset-top'
            )

        }
    }, [keyboardAdjustOpen])

    return (
        <ModalContext.Provider
            value={{
                addModal,
                clearModal,

                addSecondaryModal,
                clearSecondaryModal,

                addScroll,
                removeScroll,

                isOpen: Boolean(state.window),
                isSecondaryOpen:
                    Boolean(
                        secondaryState.window
                    ),

                key: state.key,
                secondaryKey:
                    secondaryState.key
            }}
        >
            {/* PRIMARY MODAL */}
            <div
                className={[
                    'modal',
                    'modal--primary',
                    state.window
                        ? 'modal--active'
                        : 'modal--inactive',
                    state.withScroll
                        ? 'modal--withScroll'
                        : '',
                    state.keyboardAdjust ? 'modal--keyboard-adjust' : '',
                    state.customClass ?? ''
                ]
                    .filter(Boolean)
                    .join(' ')}
                onClick={e => {
                    if (
                        e.target ===
                        e.currentTarget
                    ) {
                        clearModal()
                    }
                }}
            >
                {state.window && (
                    <ModalCard
                        state={state}
                        clearModal={
                            clearModal
                        }
                        closeLabel={t(
                            'closeModal'
                        )}
                    />
                )}
            </div>

            {/* SECONDARY / CHILD MODAL */}
            <div
                className={[
                    'modal',
                    'modal--secondary',
                    secondaryState.window
                        ? 'modal--active'
                        : 'modal--inactive',
                    secondaryState.withScroll
                        ? 'modal--withScroll'
                        : '',
                    secondaryState.keyboardAdjust ? 'modal--keyboard-adjust' : '',
                    secondaryState.customClass ??
                        ''
                ]
                    .filter(Boolean)
                    .join(' ')}
                onClick={e => {
                    if (
                        e.target ===
                        e.currentTarget
                    ) {
                        clearSecondaryModal()
                    }
                }}
            >
                {secondaryState.window && (
                    <ModalCard
                        state={
                            secondaryState
                        }
                        clearModal={
                            clearSecondaryModal
                        }
                        closeLabel={t(
                            'closeModal'
                        )}
                    />
                )}
            </div>

            {children}
        </ModalContext.Provider>
    )
}

interface ModalCardProps {
    state: ModalState
    clearModal: () => void
    closeLabel: string
}

const ModalCard = ({
    state,
    clearModal,
    closeLabel
}: ModalCardProps) => {
    const cardRef = useRef<HTMLDivElement>(null)


    // MOVE UP MODALS UNDER 45REM WHEN KEYBOARD IS OPEN
    useEffect(() => {
        if (!state.keyboardAdjust) return

        const card = cardRef.current
        if (!card) return

        const updateHeight = () => {
            const rootFontSize = parseFloat(
                getComputedStyle(document.documentElement).fontSize
            )

            const maxShortHeight = 45 * rootFontSize

            // Measure intrinsic content height, not the height
            // after the modal has been constrained by the keyboard.
            const contentHeight = Math.max(
                card.scrollHeight,
                card.offsetHeight
            )
            card.style.setProperty('--modal-card-height', `${contentHeight}px`)
            card.classList.toggle(
                'modal__card--short',
                contentHeight <= maxShortHeight
            )
        }

        const observer = new ResizeObserver(updateHeight)

        observer.observe(card)
        updateHeight()

        return () => observer.disconnect()
    }, [state.keyboardAdjust])


    return (
        <div
            ref={cardRef}
            className={`modal__card ${
                state.withoutTitle
                    ? 'modal__card__withoutTitle'
                    : ''
            }`}
        >
            {!state.withoutTitle ? (
                <h1 className='modal__title'>
                    {state.title}

                    {!state.withoutCloseIcon && (
                        <img
                            onClick={clearModal}
                            src={Icons.close}
                            alt={closeLabel}
                        />
                    )}
                </h1>
            ) : (
                !state.withoutCloseIcon && (
                    <img
                        className='modal__card__withoutTitle__close'
                        onClick={clearModal}
                        src={Icons.close}
                        alt={closeLabel}
                    />
                )
            )}

            {state.window}
        </div>
    )
}

export const useModalContext = () =>
    useContext(ModalContext)